import { useSocket } from '@/context/SocketContext';
import { BrainCircuit, Zap, Activity, Eye, TrendingUp, TrendingDown } from 'lucide-react';
import FlyBrainVisualizer from '@/components/FlyBrainVisualizer';

export function FlyBrainNurseryHUD() {
  const { flyBrainState } = useSocket();

  const dopamine = flyBrainState?.dopamine ?? 0;
  const dopNorm = Math.max(0, Math.min(1, (dopamine + 5) / 10));
  const dopColor = dopamine > 0.5 ? '#22c55e' : dopamine < -0.5 ? '#ef4444' : '#6b7280';
  const dopLabel = dopamine > 0.5 ? 'REWARD' : dopamine < -0.5 ? 'AVERSIVE' : 'NEUTRAL';

  const lastDecision = flyBrainState?.motorDecisions?.[0];

  return (
    <div className="border-b border-green-500/15 bg-[#050508] overflow-hidden">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-3 pt-2 pb-1">
        <BrainCircuit className="w-4 h-4 text-green-500/80" />
        <span className="text-[11px] font-bold uppercase tracking-widest text-green-600/90">
          FlyBrain — Market Neural OS
        </span>
        <span className={`ml-auto text-[9px] font-bold tracking-widest ${flyBrainState?.connected ? 'text-green-400' : 'text-zinc-600'}`}>
          {flyBrainState?.connected ? '● LIVE' : '○ CONNECTING'}
        </span>
      </div>

      {/* 3D Canvas */}
      <FlyBrainVisualizer compact />

      {/* Stats row */}
      <div className="grid grid-cols-4 divide-x divide-green-500/10 border-t border-green-500/10">
        {/* Dopamine */}
        <div className="flex flex-col items-center py-2 gap-1 px-2">
          <div className="flex items-center gap-1">
            <Zap className="w-3 h-3" style={{ color: dopColor }} />
            <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: dopColor }}>
              {dopLabel}
            </span>
          </div>
          <div className="w-full h-1 rounded-full bg-zinc-800 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${dopNorm * 100}%`, background: dopColor }} />
          </div>
          <span className="text-[9px] font-mono text-zinc-500">{dopamine.toFixed(2)}</span>
        </div>

        {/* Neural activity */}
        <div className="flex flex-col items-center py-2 gap-0.5 px-2">
          <div className="flex items-center gap-1 text-purple-400/70">
            <Activity className="w-3 h-3" />
            <span className="text-[9px] uppercase tracking-wider font-bold">Activity</span>
          </div>
          <span className="text-[15px] font-black tabular-nums text-purple-300/80">
            {flyBrainState?.spikeRate ?? 0}
          </span>
          <span className="text-[9px] text-zinc-600">neurons hot</span>
        </div>

        {/* Tokens watched */}
        <div className="flex flex-col items-center py-2 gap-0.5 px-2">
          <div className="flex items-center gap-1 text-cyan-500/70">
            <Eye className="w-3 h-3" />
            <span className="text-[9px] uppercase tracking-wider font-bold">Watching</span>
          </div>
          <span className="text-[15px] font-black tabular-nums text-cyan-300/80">
            {flyBrainState?.watchedTokens ?? 0}
          </span>
          <span className="text-[9px] text-zinc-600">tokens</span>
        </div>

        {/* Open positions */}
        <div className="flex flex-col items-center py-2 gap-0.5 px-2">
          <div className="flex items-center gap-1 text-amber-500/70">
            <TrendingUp className="w-3 h-3" />
            <span className="text-[9px] uppercase tracking-wider font-bold">Positions</span>
          </div>
          <span className="text-[15px] font-black tabular-nums text-amber-300/80">
            {flyBrainState?.openPositions ?? 0}
          </span>
          <span className="text-[9px] text-zinc-600">open</span>
        </div>
      </div>

      {/* Last motor decision */}
      {lastDecision && (
        <div className={`flex items-center gap-2 px-3 py-1.5 border-t border-green-500/10 ${
          lastDecision.type === 'BUY_SIGNAL' ? 'bg-green-500/5' : 'bg-red-500/5'
        }`}>
          {lastDecision.type === 'BUY_SIGNAL'
            ? <TrendingUp className="w-3 h-3 text-green-400 shrink-0" />
            : <TrendingDown className="w-3 h-3 text-red-400 shrink-0" />
          }
          <span className="text-[10px] font-mono text-zinc-400 truncate">{lastDecision.msg}</span>
          <span className="ml-auto text-[9px] text-zinc-600 shrink-0">
            {new Date(lastDecision.ts).toISOString().split('T')[1].slice(0, 8)}
          </span>
        </div>
      )}

      {/* Synapse info */}
      <div className="px-3 py-1 border-t border-green-500/8 flex items-center gap-3">
        <span className="text-[9px] text-zinc-700 font-mono">
          Synapses: {flyBrainState?.synapseCount?.toLocaleString() ?? '—'}
        </span>
        <span className="text-[9px] text-zinc-700 font-mono">
          Runtime: {flyBrainState ? Math.floor(flyBrainState.globalTime / 1000) : 0}s
        </span>
      </div>
    </div>
  );
}
